import { API_BASE_URL } from "../services/apiClient";
import { esHeic, heicAJpeg } from "./heic";

export type ImageFormat = "auto" | "jpeg" | "webp";

export interface ImageOptimizationOptions {
  /** Lado máximo (px) de la imagen resultante. */
  maxSide?: number;
  /** Lado mínimo al que aceptamos bajar para alcanzar el peso objetivo. */
  minSide?: number;
  /** Calidad base (0.1–1.0) para el encoder. */
  quality?: number;
  /** Peso objetivo en KB — si no se alcanza conservando la calidad, el
   *  optimizador prefiere calidad y queda un pelo por arriba del target. */
  targetKB?: number;
  /** Formato de salida. 'auto' = WebP si el navegador lo soporta. */
  format?: ImageFormat;
}

/** Defaults "suaves": priorizan calidad sobre tamaño. Coinciden 1:1 con
 *  `IMAGE_OPT_DEFAULTS` del backend. */
export const IMAGE_OPT_DEFAULTS: Required<ImageOptimizationOptions> = {
  maxSide: 2400,
  minSide: 800,
  quality: 0.88,
  targetKB: 800,
  format: "auto",
};

const CACHE_KEY = "imageOptimizationConfig";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

interface CachedConfig {
  data: Required<ImageOptimizationOptions>;
  at: number;
}

/** Devuelve el tamaño (bytes) estimado a partir de un dataURL base64. */
export const dataUrlBytes = (dataUrl: string) => {
  const i = dataUrl.indexOf("base64,");
  if (i === -1) return 0;
  const b64 = dataUrl.slice(i + "base64,".length);
  return Math.floor(b64.length * 0.75);
};

/** Lee el dataURL y extrae el mime, si está. */
export const dataUrlMime = (dataUrl: string): string | null => {
  const m = dataUrl.match(/^data:([^;]+);base64,/);
  return m ? m[1] : null;
};

/** Calcula dimensiones reales de un dataURL. */
export const imageDimensions = (
  dataUrl: string,
): Promise<{ width: number; height: number }> =>
  new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = rej;
    img.src = dataUrl;
  });

/**
 * Carga la imagen UNA vez. Tras `onload` pide `decode()` (si existe) para que
 * el costo de decodificar una foto de 12-48 MP ocurra aquí y no dentro de cada
 * `drawImage` de los intentos de compresión (en celulares lentos congelaba la
 * pantalla). Si `decode()` falla se ignora: `drawImage` decodifica igual.
 */
const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((res, rej) => {
    const img = new Image();
    img.onload = () => {
      const dec = (img as HTMLImageElement & { decode?: () => Promise<void> }).decode;
      if (typeof dec === "function") {
        dec.call(img).then(
          () => res(img),
          () => res(img),
        );
      } else {
        res(img);
      }
    };
    img.onerror = rej;
    img.src = src;
  });

/** Cede el hilo principal entre intentos para que la UI (spinner) siga viva. */
const cederHilo = () => new Promise<void>((r) => setTimeout(r, 0));

/**
 * Canvas REUTILIZABLE: todos los intentos de una compresión dibujan sobre el
 * mismo canvas (cambiar width/height lo limpia) en vez de crear uno nuevo y
 * re-decodificar la imagen cada vez. `liberar()` lo reduce a 0x0 para que
 * Safari iOS suelte la memoria del lienzo (tiene un tope global de canvas).
 */
const crearLienzo = (img: HTMLImageElement) => {
  const canvas = document.createElement("canvas");
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  /** Dibuja la imagen escalada para caber en maxW x maxH (nunca agranda). */
  const dibujar = (maxW: number, maxH: number) => {
    const scale = Math.min(maxW / srcW, maxH / srcH, 1);
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: false });
    if (!ctx) throw new Error("Canvas no soportado");
    ctx.drawImage(img, 0, 0, w, h);
    return canvas;
  };
  const liberar = () => {
    canvas.width = 0;
    canvas.height = 0;
  };
  return { dibujar, liberar, srcW, srcH };
};

/** Detecta si el navegador puede emitir WebP vía canvas.toDataURL. */
let _webpSupport: boolean | null = null;
export const supportsWebP = (): boolean => {
  if (_webpSupport !== null) return _webpSupport;
  try {
    const c = document.createElement("canvas");
    c.width = 1;
    c.height = 1;
    const url = c.toDataURL("image/webp");
    _webpSupport = typeof url === "string" && url.startsWith("data:image/webp");
  } catch {
    _webpSupport = false;
  }
  return _webpSupport;
};

/** Resuelve `format: 'auto'` a 'webp' o 'jpeg' según soporte. */
const resolveMime = (format: ImageFormat): "image/webp" | "image/jpeg" => {
  if (format === "webp") return "image/webp";
  if (format === "jpeg") return "image/jpeg";
  return supportsWebP() ? "image/webp" : "image/jpeg";
};

/** Un resultado de codificación: el valor (dataURL o Blob) y su peso en bytes. */
interface Candidato<T> {
  valor: T;
  bytes: number;
}

/** Codifica la imagen a maxW x maxH (sin agrandar) con el mime/calidad dados. */
type Codificador<T> = (
  maxW: number,
  maxH: number,
  mime: string,
  quality: number,
) => Promise<Candidato<T>>;

/** Busca la mayor calidad que produzca un tamaño <= targetBytes. */
const binarySearchQuality = async <T>(
  makeAtQuality: (q: number) => Promise<Candidato<T>>,
  targetBytes: number,
  qMin: number,
  qMax: number,
  steps = 6,
): Promise<Candidato<T> | null> => {
  let best: Candidato<T> | null = null;
  let lo = qMin;
  let hi = qMax;
  for (let i = 0; i < steps; i++) {
    const mid = (lo + hi) / 2;
    const d = await makeAtQuality(mid);
    if (d.bytes <= targetBytes) {
      best = d;
      hi = mid;
    } else {
      lo = mid;
    }
  }
  return best;
};

/** Fusiona opciones del caller con la config backend y los defaults. */
const mergeOptions = (
  options: ImageOptimizationOptions | undefined,
  configured: Required<ImageOptimizationOptions>,
): Required<ImageOptimizationOptions> => ({
  maxSide: options?.maxSide ?? configured.maxSide,
  minSide: options?.minSide ?? configured.minSide,
  quality: options?.quality ?? configured.quality,
  targetKB: options?.targetKB ?? configured.targetKB,
  format: options?.format ?? configured.format,
});

/**
 * Lee la configuración de optimización desde el backend con cache en
 * sessionStorage (TTL 5 min). Si el backend no responde, usa defaults.
 *
 * `force=true` ignora el cache — útil cuando el admin acaba de guardar
 * nuevos parámetros en la página de configuración.
 */
export const getImageOptimizationConfig = async (
  force = false,
): Promise<Required<ImageOptimizationOptions>> => {
  if (!force) {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw) as CachedConfig;
        if (Date.now() - cached.at < CACHE_TTL_MS && cached.data) {
          return { ...IMAGE_OPT_DEFAULTS, ...cached.data };
        }
      }
    } catch {
      // cache corrupto — lo ignoramos y pedimos fresco
    }
  }

  // Llamada directa a `fetch` (no `apiFetch`) para evitar el toast global
  // de error si el endpoint aún no está desplegado o falla temporalmente.
  // En ese caso caemos silenciosamente a defaults — la compresión sigue
  // funcionando con los valores por defecto del frontend. La auth viaja
  // por cookie HttpOnly (`credentials: "include"` lo agrega el interceptor
  // global). Round 2026-04-26 fase 6 (P1-B): se removió `Authorization: Bearer`.
  let res:
    | {
        data: Required<ImageOptimizationOptions>;
        defaults: Required<ImageOptimizationOptions>;
      }
    | null = null;
  try {
    const r = await fetch(`${API_BASE_URL}/config-options/image-optimization`);
    if (r.ok) res = await r.json();
  } catch {
    res = null;
  }

  const data = res?.data
    ? ({ ...IMAGE_OPT_DEFAULTS, ...res.data } as Required<ImageOptimizationOptions>)
    : IMAGE_OPT_DEFAULTS;

  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, at: Date.now() } satisfies CachedConfig),
    );
  } catch {
    // sessionStorage no disponible (Safari privado, quota)
  }
  return data;
};

/** Limpia el cache — usarlo tras guardar una nueva config. */
export const invalidateImageOptimizationConfig = () => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* noop */
  }
};

/**
 * Algoritmo de compresión (independiente de la fuente y del formato de salida:
 * dataURL o Blob). Pasos:
 *   1) Downscale a maxSide + encoder en quality configurada.
 *   2) Si aún supera el target, binary-search de quality en [max(0.55, q-0.3), q].
 *   3) Si aún supera, downscale progresivo (85%) hasta minSide.
 *   4) Último intento en minSide con calidad mínima aceptable.
 *
 * NUNCA falla por no alcanzar el target: devuelve la mejor aproximación
 * (prioriza calidad por encima del tamaño exacto).
 */
const comprimirConAlgoritmo = async <T>(
  codificar: Codificador<T>,
  p: {
    maxSide: number;
    minSide: number;
    quality: number;
    targetBytes: number;
    mime: string;
  },
): Promise<T> => {
  const { maxSide, minSide, quality, targetBytes, mime } = p;
  const cod: Codificador<T> = async (w, h, m, q) => {
    await cederHilo();
    return codificar(w, h, m, q);
  };

  // Paso 1: downscale a maxSide + calidad configurada.
  const current = await cod(maxSide, maxSide, mime, quality);
  if (current.bytes <= targetBytes) return current.valor;

  // Paso 2: binary-search de calidad dentro de una ventana suave (no
  // bajamos menos de 0.55 en el primer intento — evita degradación visible).
  const qLow = Math.max(0.55, quality - 0.3);
  const qBest = await binarySearchQuality(
    (q) => cod(maxSide, maxSide, mime, q),
    targetBytes,
    qLow,
    quality,
    6,
  );
  if (qBest) return qBest.valor;

  // Paso 3: downscale progresivo (pasos del 85%) manteniendo calidad base.
  // Si al llegar a minSide seguimos por arriba del target, devolvemos el
  // mejor candidato encontrado — priorizamos calidad sobre target exacto.
  let side = Math.floor(maxSide * 0.85);
  while (side >= minSide) {
    const scaled = await cod(side, side, mime, quality);
    if (scaled.bytes <= targetBytes) return scaled.valor;

    const lado = side;
    const scaledBest = await binarySearchQuality(
      (q) => cod(lado, lado, mime, q),
      targetBytes,
      Math.max(0.5, quality - 0.35),
      quality,
      5,
    );
    if (scaledBest) return scaledBest.valor;

    side = Math.floor(side * 0.85);
  }

  // Paso 4 (excepción): un último intento en minSide con calidad mínima
  // aceptable. Si aún no alcanza, devolvemos el mejor intento anterior —
  // queremos entregar SIEMPRE una imagen, aunque supere el target.
  const lastResort = await cod(minSide, minSide, mime, Math.max(0.5, quality - 0.3));
  return lastResort.bytes < current.bytes ? lastResort.valor : current.valor;
};

/**
 * Comprime un dataURL aplicando los parámetros configurados (o los que
 * venga en `options`). Si el dataURL ya pesa menos que el target y cabe en
 * maxSide, se retorna tal cual (short-circuit). Ver `comprimirConAlgoritmo`.
 *
 * Decodifica la imagen UNA sola vez y reutiliza un canvas entre intentos.
 * NUNCA retorna null.
 */
export const compressImage = async (
  dataUrl: string,
  options?: ImageOptimizationOptions,
): Promise<string | null> => {
  const configured = await getImageOptimizationConfig();
  const { maxSide, minSide, quality, targetKB, format } = mergeOptions(
    options,
    configured,
  );

  const mime = resolveMime(format);
  const targetBytes = targetKB * 1024;

  const img = await loadImage(dataUrl);
  const lienzo = crearLienzo(img);
  try {
    // Short-circuit: la imagen ya es pequeña y no necesita downscale.
    if (
      dataUrlBytes(dataUrl) <= targetBytes &&
      lienzo.srcW <= maxSide &&
      lienzo.srcH <= maxSide
    ) {
      return dataUrl;
    }
    return await comprimirConAlgoritmo<string>(
      async (w, h, m, q) => {
        const url = lienzo.dibujar(w, h).toDataURL(m, q);
        return { valor: url, bytes: dataUrlBytes(url) };
      },
      { maxSide, minSide, quality, targetBytes, mime },
    );
  } finally {
    lienzo.liberar();
    img.removeAttribute("src");
  }
};

/**
 * Convierte un dataURL en un `File` listo para FormData. Útil cuando un
 * componente externo (e.g. ImageEditorModal de Filerobot) entrega un
 * dataURL ya procesado y necesitamos pasarlo a `uploadMediaFile`.
 *
 * El nombre se ajusta agregando la extensión correcta según el MIME del
 * dataURL si no la tiene.
 */
export const dataUrlToFile = (dataUrl: string, baseName: string): File => {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] ?? "image/jpeg";
  const bin = atob(base64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  const blob = new Blob([arr], { type: mime });

  const ext =
    mime === "image/webp" ? "webp" :
    mime === "image/jpeg" ? "jpg" :
    mime === "image/png" ? "png" :
    mime.split("/")[1] || "img";
  const stem = baseName.replace(/\.[^.]+$/, "");
  return new File([blob], `${stem}.${ext}`, { type: mime });
};

/** canvas.toBlob como promesa (null = el navegador no pudo codificar). */
const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mime: string,
  quality: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("No se pudo codificar la imagen"))),
      mime,
      quality,
    );
  });

/**
 * Comprime un `File` (solo si es imagen) aplicando los parámetros de la
 * config admin. Si no es imagen (video u otro), devuelve el File tal cual.
 *
 * Útil para los flujos de subida directa: en vez de manipular dataURL/Blob
 * en cada consumidor, se pasa el File a este helper y se obtiene un File
 * listo para FormData. Conserva el nombre original (con extensión ajustada
 * al mime resultante).
 *
 * Optimizado para celulares lentos (round 2026-10-26): NO pasa por dataURL
 * (la lectura base64 sumaba +33% de memoria): la imagen se decodifica UNA vez
 * desde un object URL y cada intento codifica con `canvas.toBlob` (asíncrono,
 * fuera del hilo principal) sobre un canvas reutilizado. El algoritmo y los
 * parámetros son los de `compressImage`; solo cambia que el peso se mide con
 * `blob.size` exacto en vez del estimado `base64.length * 0.75` (±2 bytes).
 */
export const compressImageFile = async (
  file: File,
  options?: ImageOptimizationOptions,
): Promise<File> => {
  if (!file.type.startsWith("image/")) return file;

  const configured = await getImageOptimizationConfig();
  const { maxSide, minSide, quality, targetKB, format } = mergeOptions(
    options,
    configured,
  );
  const mimeSalida = resolveMime(format);
  const targetBytes = targetKB * 1024;

  let origen: File = file;
  let objectUrl = URL.createObjectURL(origen);
  let img: HTMLImageElement | null = null;
  let lienzo: ReturnType<typeof crearLienzo> | null = null;
  let resultado: Blob;
  try {
    try {
      img = await loadImage(objectUrl);
    } catch (e) {
      // Una foto HEIC que este navegador no decodifica (todos menos Safari) se
      // convierte a JPEG y se sigue con el flujo normal. Cualquier otra imagen
      // que no se pueda leer sigue fallando como siempre.
      if (!esHeic(file)) throw e;
      URL.revokeObjectURL(objectUrl);
      origen = await heicAJpeg(file);
      objectUrl = URL.createObjectURL(origen);
      img = await loadImage(objectUrl);
    }
    lienzo = crearLienzo(img);
    const l = lienzo;
    // Un HEIC que el navegador sí decodificó (Safari) nunca se conserva tal cual
    // aunque sea liviano: el servidor no lo acepta, así que siempre se recodifica.
    if (!esHeic(origen) && origen.size <= targetBytes && l.srcW <= maxSide && l.srcH <= maxSide) {
      // Short-circuit: ya es pequeña y cabe; se conserva el contenido original.
      resultado = origen;
    } else {
      resultado = await comprimirConAlgoritmo<Blob>(
        async (w, h, m, q) => {
          const blob = await canvasToBlob(l.dibujar(w, h), m, q);
          return { valor: blob, bytes: blob.size };
        },
        { maxSide, minSide, quality, targetBytes, mime: mimeSalida },
      );
    }
  } finally {
    lienzo?.liberar();
    img?.removeAttribute("src");
    URL.revokeObjectURL(objectUrl);
  }

  // Ajustar la extensión del nombre al mime resultante (e.g. .jpg → .webp)
  const mime = resultado.type || file.type;
  const ext = mime === "image/webp" ? "webp" : mime === "image/jpeg" ? "jpg" : mime.split("/")[1] || "img";
  const baseName = file.name.replace(/\.[^.]+$/, "");
  return new File([resultado], `${baseName}.${ext}`, { type: mime, lastModified: file.lastModified });
};
